import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { meQuery } from '../api/me.ts';
import {
  createVenue,
  KIND_LABELS,
  pickerVenues,
  type VenueInput,
  type VenueKind,
  venuesQuery,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ VEN-01 at v1.951: the venue on a quote or a job, the same control on both. Someone who may
// change it picks from the venue list; everyone else sees the name. Choosing saves at once, like the
// price group on a client, because it is one field with nothing to confirm.
//
// VEN-01 at v1.971 (venue slice 1, part A): your own venues come first, a one-off site appears only
// once chosen, and someone who may manage venues can add one from here with a name and an address.

export function VenueField({
  venue,
  editable,
  saving,
  error,
  onChange,
}: {
  venue: { id: string; name: string } | null;
  editable: boolean;
  saving: boolean;
  error: Error | null;
  onChange: (venueId: string | null) => void;
}) {
  return (
    <section className="flex flex-col gap-2" aria-labelledby="venue">
      <h2 id="venue" className="section-title">
        Venue
      </h2>
      {editable ? (
        <div className="max-w-md">
          <VenuePicker
            id="venue-field"
            label="Venue"
            hideLabel
            chosen={venue}
            noneLabel="No venue named"
            disabled={saving}
            onChange={onChange}
          />
        </div>
      ) : (
        <p>{venue?.name ?? 'No venue named'}</p>
      )}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}

/** A venue typed into the picker's add form but not added yet. */
export type UnaddedVenue = Pick<VenueInput, 'name' | 'address' | 'kind'>;

export function VenuePicker({
  id,
  label,
  hideLabel = false,
  chosen,
  noneLabel,
  disabled = false,
  onChange,
  onNaming,
}: {
  id: string;
  label: string;
  hideLabel?: boolean;
  chosen: { id: string; name: string } | null;
  noneLabel: string;
  disabled?: boolean;
  onChange: (venueId: string | null) => void;
  /**
   * A venue named in the add form but not yet added, or null. A form with its own submit button adds
   * it on submit, so pressing that button with a name typed does not quietly drop it.
   */
  onNaming?: (venue: UnaddedVenue | null) => void;
}) {
  const venues = useQuery(venuesQuery);
  const me = useQuery(meQuery);
  const mayAdd = me.data?.capabilities.includes('venues.manage') ?? false;
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [draft, setDraft] = useState<{ name: string; address: string; kind: VenueKind }>({
    name: '',
    address: '',
    kind: 'venue',
  });
  const [problem, setProblem] = useState<string | undefined>();

  const options = pickerVenues(venues.data ?? [], chosen?.id ?? null);
  const mine = options.filter((v) => v.mine);
  const others = options.filter((v) => !v.mine);
  // A venue retired since it was named still shows as chosen.
  const retired = chosen && !options.some((v) => v.id === chosen.id) ? chosen : null;

  const report = (next: typeof draft, open: boolean) =>
    onNaming?.(
      open && next.name.trim()
        ? { name: next.name.trim(), address: next.address.trim() || null, kind: next.kind }
        : null,
    );
  const edit = (changes: Partial<typeof draft>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    report(next, true);
  };
  const close = () => {
    setNaming(false);
    setDraft({ name: '', address: '', kind: 'venue' });
    setProblem(undefined);
    onNaming?.(null);
  };

  const add = useMutation({
    mutationFn: () =>
      createVenue({
        name: draft.name.trim(),
        address: draft.address.trim() || null,
        kind: draft.kind,
      }),
    onSuccess: (venue) => {
      // In the list at once, so the picker can show it chosen before the list is fetched again.
      const listed = { ...venue, roomsCount: 0, suburb: null };
      queryClient.setQueryData(venuesQuery.queryKey, (now) => (now ? [...now, listed] : [listed]));
      close();
      onChange(venue.id);
      void queryClient.invalidateQueries({ queryKey: venuesQuery.queryKey });
    },
  });

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-xs text-ink-soft'}>
        {label}
      </label>
      <select
        id={id}
        className="input"
        value={naming ? '+' : (chosen?.id ?? '')}
        disabled={disabled || venues.isPending}
        onChange={(e) => {
          const value = e.currentTarget.value;
          if (value === '+') {
            setNaming(true);
            return report(draft, true);
          }
          if (naming) close();
          onChange(value || null);
        }}
      >
        <option value="">{noneLabel}</option>
        {retired ? <option value={retired.id}>{retired.name}</option> : null}
        {mine.length > 0 ? (
          <optgroup label="Your venues">
            {mine.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </optgroup>
        ) : null}
        {mine.length > 0 && others.length > 0 ? (
          <optgroup label="Other venues">
            {others.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </optgroup>
        ) : (
          others.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))
        )}
        {mayAdd ? <option value="+">+ New venue</option> : null}
      </select>
      {venues.data?.length === 0 && !mayAdd ? (
        <span className="text-xs text-muted">No venues yet. Add them under Venues.</span>
      ) : null}
      {naming ? (
        <div className="flex flex-col gap-3 rounded-md border-2 border-divider p-3">
          <Field
            label="New venue’s name"
            value={draft.name}
            error={problem}
            onChange={(e) => edit({ name: e.currentTarget.value })}
          />
          <Field
            label="Address"
            value={draft.address}
            onChange={(e) => edit({ address: e.currentTarget.value })}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-kind`} className="text-xs text-ink-soft">
              What kind of place
            </label>
            <select
              id={`${id}-kind`}
              className="input"
              value={draft.kind}
              onChange={(e) => edit({ kind: e.currentTarget.value as VenueKind })}
            >
              {(Object.keys(KIND_LABELS) as VenueKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          {add.error ? (
            <p role="alert" className="text-sm font-semibold text-accent-2-700">
              {add.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="quiet"
              disabled={add.isPending}
              onClick={() => {
                // Said, not a greyed button (CB-REQ ADO-06).
                if (!draft.name.trim()) return setProblem('Name the venue.');
                setProblem(undefined);
                add.mutate();
              }}
            >
              Add venue
            </Button>
            <Button type="button" variant="quiet" onClick={close}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
